import { useEffect, useMemo, useState } from "react";
import type { POI, Schedule } from "@poi-planner/shared";
import { useLibraryStore } from "../store/libraryStore";
import { useCollectionsStore } from "../store/collectionsStore";
import { useUIStore } from "../store/uiStore";
import { useTripStore } from "../store/tripStore";
import { useAuthStore } from "../store/authStore";
import { useTripSidebarFilterStore } from "../store/tripSidebarFilterStore";
import { buildLegPoiMap, useLegStore } from "../store/legStore";
import type { LegOption } from "../components/PoiFilterBar";
import {
  usePoiFilters,
  type ActiveFilterCounts,
  type FilterFacet,
  type MapBounds,
  type OwnerOption,
  type SortMode,
  type TagFacet,
  type TagFilterMode,
  type TagMatchMode,
  type TagWithCount,
} from "./usePoiFilters";

export const NO_LEG_FILTER_ID = "__no_leg__";

export interface TripSidebarController {
  hideScheduled: boolean;
  setHideScheduled: (value: boolean) => void;
  targetPoiId: string | null;
  setTargetPoiId: (poiId: string | null) => void;
  targetPoi: POI | null;
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagMatchMode: TagMatchMode;
  tagFilters: Map<string, TagFilterMode>;
  sourceFilters: Map<string, TagFilterMode>;
  collectionFilters: Map<string, TagFilterMode>;
  legFilters: Map<string, TagFilterMode>;
  ownerFilters: Map<string, TagFilterMode>;
  maximumDistanceKm: number | null;
  allTags: TagWithCount[];
  filteredPOIs: POI[];
  distanceMap: Map<string, number>;
  allSourceTexts: string[];
  ownerOptions: OwnerOption[];
  activeFilterCounts: ActiveFilterCounts;
  /** Live per-option counts for the phone Filters dialog. */
  tagFacets: TagFacet[];
  legFacetCounts: Map<string, number>;
  collectionOptions: { id: string; name: string }[];
  legOptions: LegOption[];
  scheduledCounts: Map<string, number>;
  // Pin actions
  pinnedPoiIds: Set<string>;
  hasLegs: boolean;
  onTogglePin: (poiId: string) => Promise<string | null>;
  toggleLeg: (legId: string) => void;
  setSearch: (value: string) => void;
  setSortMode: (mode: SortMode) => void;
  setSortAscending: (ascending: boolean) => void;
  setTagMatchMode: (mode: TagMatchMode) => void;
  setMaximumDistanceKm: (distanceKm: number | null) => void;
  toggleTag: (tag: string) => void;
  toggleSource: (source: string) => void;
  toggleCollection: (collectionId: string) => void;
  toggleOwner: (ownerId: string) => void;
  clearAllFilters: () => void;
  removeFilter: (facet: FilterFacet, key: string) => void;
  totalPoiCount: number;
}

interface UseTripSidebarControllerOptions {
  boundsFilterEnabled?: boolean;
  mapBounds?: MapBounds | null;
}

export function useTripSidebarController(
  pois: POI[],
  schedule: Schedule,
  options: UseTripSidebarControllerOptions = {},
): TripSidebarController {
  const { boundsFilterEnabled = false, mapBounds = null } = options;
  // Filters live in a store so they survive the calendar <-> map remount.
  const legs = useLegStore((s) => s.legs);
  const hideScheduled = useTripSidebarFilterStore((s) => s.hideScheduled);
  const setHideScheduled = useTripSidebarFilterStore((s) => s.setHideScheduled);
  const sharedFilters = useTripSidebarFilterStore((s) => s.filters);
  const updateSharedFilters = useTripSidebarFilterStore((s) => s.updateFilters);
  const filterStateBinding = useMemo(
    () => ({ state: sharedFilters, update: updateSharedFilters }),
    [sharedFilters, updateSharedFilters],
  );
  const [prevSortMode, setPrevSortMode] =
    useState<Exclude<SortMode, "distance">>("alpha");

  const targetPoiId = useUIStore((s) => s.targetPoiId);
  const setTargetPoiId = useUIStore((s) => s.setTargetPoiId);
  const tags = useLibraryStore((s) => s.tags);
  const categoryTags = useLibraryStore((s) => s.categoryTags);
  const collections = useCollectionsStore((s) => s.collections);
  const pinnedPoiIds = useTripStore((s) => s.pinnedPoiIds);
  const pinPoi = useTripStore((s) => s.pinPoi);
  const unpinPoi = useTripStore((s) => s.unpinPoi);
  const currentUserId = useAuthStore((s) => s.userId);

  const collectionPoiMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const collection of collections) {
      map.set(collection.id, new Set(collection.poiIds));
    }
    return map;
  }, [collections]);

  const collectionOptions = useMemo(() => {
    return collections.map((c) => ({ id: c.id, name: c.name }));
  }, [collections]);

  const scheduledCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const sp of schedule.scheduledPOIs) {
      counts.set(sp.poiId, (counts.get(sp.poiId) ?? 0) + 1);
    }
    return counts;
  }, [schedule]);

  // A leg bar's focus button asks the sidebar to show only that leg, which
  // the leg pills express on their own.
  const pendingLegFocusId = useUIStore((s) => s.pendingLegFocusId);
  const setSidebarLegFocus = useUIStore((s) => s.setSidebarLegFocus);
  useEffect(() => {
    if (!pendingLegFocusId) return;
    updateSharedFilters(() => ({
      legFilters: new Map([[pendingLegFocusId, "include"]]),
    }));
    setSidebarLegFocus(null);
  }, [pendingLegFocusId, updateSharedFilters, setSidebarLegFocus]);

  /**
   * Every POI attached to this trip, not just the pinned ones. A hotel can be
   * assigned as a night's sleep (or a place scheduled) without ever being
   * pinned, and such a POI belongs to no leg — deriving the facet from pins
   * alone left it out of legPoiMap entirely, so any leg filter hid it with no
   * facet able to bring it back.
   */
  const tripPoiIds = useMemo(() => {
    const ids = new Set(pinnedPoiIds);
    for (const sp of schedule.scheduledPOIs) ids.add(sp.poiId);
    for (const poiId of Object.values(schedule.sleep)) {
      if (poiId) ids.add(poiId);
    }
    return ids;
  }, [pinnedPoiIds, schedule]);

  const noLegPoiIds = useMemo(() => {
    const assigned = new Set(legs.flatMap((leg) => leg.poiIds));
    return new Set([...tripPoiIds].filter((poiId) => !assigned.has(poiId)));
  }, [legs, tripPoiIds]);

  /**
   * A leg filter for a leg that no longer exists renders neither a pill nor a
   * removable chip, so it would silently hide every place with no way to clear
   * it. Drop those entries however the leg disappeared — deleted here, deleted
   * by a collaborator, or gone after a trip reload.
   */
  useEffect(() => {
    const validLegIds = new Set(legs.map((leg) => leg.id));
    if (noLegPoiIds.size > 0) validLegIds.add(NO_LEG_FILTER_ID);
    const stale = [...sharedFilters.legFilters.keys()].filter(
      (legId) => !validLegIds.has(legId),
    );
    if (stale.length === 0) return;
    updateSharedFilters((prev) => {
      const legFilters = new Map(prev.legFilters);
      for (const legId of stale) legFilters.delete(legId);
      return { legFilters };
    });
  }, [legs, noLegPoiIds, sharedFilters.legFilters, updateSharedFilters]);

  const legPoiMap = useMemo(() => {
    const map = buildLegPoiMap(legs);
    for (const poiId of noLegPoiIds) map.set(poiId, [NO_LEG_FILTER_ID]);
    return map;
  }, [legs, noLegPoiIds]);
  const legOptions = useMemo<LegOption[]>(
    () => [
      ...legs.map((leg) => ({
        id: leg.id,
        name: leg.name,
        color: leg.color,
      })),
      ...(noLegPoiIds.size > 0
        ? [
            {
              id: NO_LEG_FILTER_ID,
              name: "No leg",
              description: "In this trip but not assigned to a leg",
            },
          ]
        : []),
    ],
    [legs, noLegPoiIds.size],
  );

  const targetPoi = useMemo(() => {
    if (!targetPoiId) return null;
    return pois.find((p) => p.id === targetPoiId) ?? null;
  }, [pois, targetPoiId]);

  const {
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    legFilters,
    ownerFilters,
    maximumDistanceKm,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    ownerOptions,
    activeFilterCounts,
    tagFacets,
    legFacetCounts,
    setSearch,
    setSortMode,
    setSortAscending,
    setTagMatchMode,
    setMaximumDistanceKm,
    toggleTag,
    toggleSource,
    toggleCollection,
    toggleLeg,
    toggleOwner,
    clearAllFilters: clearPoiFilters,
    removeFilter,
  } = usePoiFilters(pois, {
    hideScheduled: {
      enabled: hideScheduled,
      scheduledCounts,
    },
    distanceTarget: {
      targetPoiId,
      allPois: pois,
    },
    collectionPoiMap,
    legPoiMap,
    boundsFilter: {
      enabled: boundsFilterEnabled,
      bounds: mapBounds,
    },
    ownerContext: {
      currentUserId,
    },
    tagContext: { tags, categoryTags, currentUserId },
    stateBinding: filterStateBinding,
  });

  useEffect(() => {
    if (targetPoiId) {
      if (sortMode !== "distance") {
        setPrevSortMode(sortMode as Exclude<SortMode, "distance">);
        setSortMode("distance");
      }
    } else if (sortMode === "distance") {
      setSortMode(prevSortMode);
    }
  }, [targetPoiId, sortMode, prevSortMode, setSortMode]);

  const onTogglePin = async (poiId: string): Promise<string | null> => {
    if (pinnedPoiIds.has(poiId)) {
      if (legs.length > 0) {
        useUIStore.getState().openPoiLegPicker({
          kind: "manage-pin",
          poiId,
        });
        return null;
      }
      if ((scheduledCounts.get(poiId) ?? 0) > 0) {
        return "Remove from day first before unpinning";
      }
      return (await unpinPoi(poiId)) ? null : "Could not remove pin";
    }

    if (!(await pinPoi(poiId))) return "Could not pin to trip";
    if (legs.length > 0) {
      useUIStore.getState().openPoiLegPicker({
        kind: "manage-pin",
        poiId,
      });
    }
    return null;
  };

  return {
    hideScheduled,
    setHideScheduled,
    targetPoiId,
    setTargetPoiId,
    targetPoi,
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    legFilters,
    ownerFilters,
    maximumDistanceKm,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    ownerOptions,
    activeFilterCounts,
    tagFacets,
    legFacetCounts,
    collectionOptions,
    legOptions,
    toggleLeg,
    scheduledCounts,
    pinnedPoiIds,
    hasLegs: legs.length > 0,
    onTogglePin,
    setSearch,
    setSortMode,
    setSortAscending,
    setTagMatchMode,
    setMaximumDistanceKm,
    toggleTag,
    toggleSource,
    toggleCollection,
    toggleOwner,
    clearAllFilters: () => {
      setSearch("");
      clearPoiFilters();
    },
    removeFilter,
    totalPoiCount: pois.length,
  };
}
